import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import AuthLayout from './AuthLayout';
import Input from '../ui/Input';
import Button from '../ui/Button';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  
  const { resetPassword, error: authError, clearError } = useAuth();

  const handleEmailChange = (value) => {
    setEmail(value);
    
    // Clear errors when user starts typing
    if (errors.email) {
      setErrors(prev => ({
        ...prev,
        email: ''
      }));
    }
    
    if (authError) {
      clearError();
    }
    
    if (success) {
      setSuccess(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    
    try {
      await resetPassword(email);
      setSuccess(true);
      console.log('Password reset email sent successfully');
    } catch (error) {
      console.error('Password reset failed:', error);
      // Error is handled by AuthContext
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <AuthLayout 
        title="Check Your Email" 
        subtitle="Password reset instructions sent"
      >
        <div className="text-center space-y-4">
          {/* Success Icon */}
          <div className="flex justify-center">
            <div className="w-16 h-16 bg-success-subtle rounded-full flex items-center justify-center">
              <svg className="w-8 h-8 text-success" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
          </div>

          {/* Success Message */}
          <div className="space-y-2">
            <h3 className="text-lg font-semibold text-text-primary">Email Sent!</h3>
            <p className="text-text-secondary text-sm">
              We've sent password reset instructions to <strong>{email}</strong>
            </p>
            <p className="text-text-tertiary text-xs">
              If you don't see the email, check your spam folder.
            </p>
          </div>

          {/* Additional Instructions */}
          <div className="bg-info-subtle border border-border-primary rounded-lg p-4">
            <div className="flex items-start">
              <svg className="w-5 h-5 text-info mt-0.5 mr-3" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              <div className="text-sm text-info">
                <p className="font-medium mb-1">Next Steps:</p>
                <ul className="text-xs space-y-1">
                  <li>• Check your email inbox for the reset link</li>
                  <li>• Click the link to create a new password</li>
                  <li>• The link will expire in 1 hour for security</li>
                  <li>• You can then sign in with your new password</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="space-y-3 pt-4">
            <Link to="/login">
              <Button className="w-full" size="md">
                Back to Sign In
              </Button>
            </Link>
            
            <button
              onClick={() => {
                setSuccess(false);
                setEmail('');
              }}
              className="text-sm text-text-link hover:text-text-link-hover transition-colors"
            >
              Send to a different email
            </button>
          </div>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout 
      title="Reset Password" 
      subtitle="Enter your email to receive reset instructions"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Email */}
        <Input
          label="Email Address"
          type="email"
          value={email}
          onChange={(e) => handleEmailChange(e.target.value)}
          placeholder="Enter your email address"
          error={errors.email}
          required
          autoComplete="email"
          className="text-sm"
        />

        {/* Auth Error with enhanced status handling */}
        {authError && (
          <div className="bg-error-subtle border border-border-error rounded-lg p-3">
            <div className="flex items-start">
              <svg className="w-4 h-4 text-error mr-2 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <div className="text-sm text-error">
                <p className="font-medium">{getErrorTitle(authError)}</p>
                <p className="text-xs mt-1">{authError}</p>
                {authError.includes('pending') && (
                  <div className="mt-2 text-xs">
                    <p>• Your account needs to be approved first</p>
                    <p>• Contact administrator for assistance</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Account Status Help */}
        {authError?.includes('pending') && (
          <div className="bg-warning-subtle border border-border-primary rounded-lg p-3">
            <div className="flex items-start">
              <svg className="w-4 h-4 text-warning mr-2 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <div className="text-sm text-warning">
                <p className="font-medium mb-1">Account Pending Approval</p>
                <p className="text-xs">Password reset is not available until your account is activated by an administrator.</p>
              </div>
            </div>
          </div>
        )}

        {/* Info Message */}
        <div className="bg-info-subtle border border-border-primary rounded-lg p-3">
          <div className="flex items-start">
            <svg className="w-4 h-4 text-info mt-0.5 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
            <div className="text-sm text-info">
              <p className="font-medium mb-1">What happens next?</p>
              <ul className="space-y-1 text-xs">
                <li>• We'll send a password reset link to your email</li>
                <li>• Click the link to create a new password</li>
                <li>• The link will expire in 1 hour for security</li>
                <li>• Only active accounts can reset passwords</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <Button
          type="submit"
          loading={loading}
          disabled={loading}
          className="w-full"
          size="md"
        >
          {loading ? 'Sending...' : 'Send Reset Instructions'}
        </Button>

        {/* Back to Login */}
        <div className="text-center pt-4 border-t border-border-primary">
          <p className="text-sm text-text-secondary">
            Remember your password?{' '}
            <Link 
              to="/login"
              className="text-text-link hover:text-text-link-hover font-medium transition-colors"
            >
              Sign in here
            </Link>
          </p>
        </div>
      </form>
    </AuthLayout>
  );
};

// Helper function to get error title based on error message
const getErrorTitle = (errorMessage) => {
  if (errorMessage.includes('pending')) return 'Account Not Activated';
  if (errorMessage.includes('No account found')) return 'Account Not Found';
  if (errorMessage.includes('only available for active')) return 'Reset Not Available';
  return 'Reset Failed';
};

export default ForgotPassword;